import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import { toFrequencyCode } from '../lib/frequencyMap';
import { useAddBudgetItem, useUpdateBudgetItem, useDeleteBudgetItem } from '../lib/hooks/useBudgetQueries';
import { useToast } from '../lib/toastContext';
import { useConfirm } from './ConfirmDialog';

interface BudgetItemInputForm { itemType: string; itemSubType: string; description: string; amount: string | number; frequency: string; }
const DEFAULTS: BudgetItemInputForm = { itemType: 'income', itemSubType: '', description: '', amount: '', frequency: 'F' };
interface Props { mode: 'create' | 'edit'; initial?: any; fixedType?: string; onSuccess?: (r:any)=>void; enableDelete?: boolean; }

export default function BudgetItemForm({ mode, initial, fixedType, onSuccess, enableDelete }: Props){
  const { showSuccess, showError } = useToast();
  const { confirm, ConfirmDialogPortal } = useConfirm();
  const [values, setValues] = useState<BudgetItemInputForm>({ ...DEFAULTS, ...initial, itemType: fixedType || (initial && initial.itemType) || DEFAULTS.itemType });
  const [touched, setTouched] = useState<Partial<Record<keyof BudgetItemInputForm, boolean>>>({});
  const updateMutation = useUpdateBudgetItem(initial?.itemId ?? '');
  const addMutation = useAddBudgetItem();
  const deleteMutation = useDeleteBudgetItem(initial?.itemId ?? '');

  useEffect(()=>{ setValues(v=> ({ ...v, ...initial, itemType: fixedType || initial?.itemType || v.itemType })); },[initial, fixedType]);

  const update = <K extends keyof BudgetItemInputForm>(k: K, val: BudgetItemInputForm[K]) => {
    setValues(v => ({ ...v, [k]: val }));
    setTouched(t => ({ ...t, [k]: true }));
  };

  const isSubmitting = !!(addMutation.isPending || updateMutation.isPending);
  const isDeleting = !!deleteMutation.isPending;

  // Field-level validation
  const subtypeError = touched.itemSubType && values.itemSubType.trim().length < 2 ? 'Required (min 2 characters)' : '';
  const amountError = touched.amount && (values.amount === '' || isNaN(Number(values.amount)) || Number(values.amount) < 0) ? 'Must be a positive number' : '';
  const isValid = values.itemSubType.trim().length >= 2 && values.amount !== '' && !isNaN(Number(values.amount)) && Number(values.amount) >= 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ itemSubType: true, amount: true });
    if (!isValid) return;
    const body = { itemType: values.itemType, itemSubType: values.itemSubType.trim(), description: values.description.trim(), amount: Number(values.amount), frequency: toFrequencyCode(values.frequency) };
    try {
      if (mode === 'create') {
        const r = await addMutation.mutateAsync(body);
        showSuccess('Item created');
        onSuccess?.(r);
      } else if (mode === 'edit' && initial?.itemId) {
        const r = await updateMutation.mutateAsync(body);
        showSuccess('Item saved');
        onSuccess?.(r);
      }
    } catch (err: any) {
      showError(err?.message || 'Failed to save item');
    }
  };

  const handleDelete = async () => {
    if (!initial?.itemId) return;
    const ok = await confirm({ message: 'Delete this item?', danger: true, confirmLabel: 'Delete' });
    if (!ok) return;
    try {
      const r = await deleteMutation.mutateAsync({});
      showSuccess('Item deleted');
      onSuccess?.(r);
    } catch (err: any) {
      showError(err?.message || 'Failed to delete item');
    }
  };

  return (
    <>
    <Paper component="form" onSubmit={handleSubmit} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 520 }}>
      <Stack spacing={2}>
        {!fixedType && (
          <TextField select label="Category" value={values.itemType} onChange={(e)=>update('itemType', e.target.value)} fullWidth size="small">
            <MenuItem value="income">Income</MenuItem>
            <MenuItem value="savings">Savings</MenuItem>
            <MenuItem value="expense">Expense</MenuItem>
          </TextField>
        )}
        <TextField
          label="Type"
          value={values.itemSubType}
          onChange={(e)=>update('itemSubType', e.target.value)}
          placeholder="e.g. Salary"
          fullWidth size="small"
          error={!!subtypeError}
          helperText={subtypeError || 'A short label for this item'}
        />
        <TextField
          label="Description"
          value={values.description}
          onChange={(e)=>update('description', e.target.value)}
          placeholder="Optional notes"
          fullWidth size="small"
        />
        <TextField
          label="Amount"
          value={values.amount}
          onChange={(e)=>update('amount', e.target.value)}
          placeholder="0.00"
          fullWidth size="small"
          error={!!amountError}
          helperText={amountError}
          inputProps={{ inputMode: 'decimal' }}
        />
        <TextField select label="Frequency" value={values.frequency} onChange={(e)=>update('frequency', e.target.value)} fullWidth size="small">
          <MenuItem value="W">Weekly</MenuItem>
          <MenuItem value="F">Fortnightly</MenuItem>
          <MenuItem value="M">Monthly</MenuItem>
          <MenuItem value="Q">Quarterly</MenuItem>
          <MenuItem value="Y">Yearly</MenuItem>
        </TextField>
        {!isValid && Object.values(touched).some(Boolean) && (
          <FormHelperText error>Please fix the errors above before saving.</FormHelperText>
        )}
        <Stack spacing={1}>
          <Button type="submit" variant="contained" disabled={!isValid || isSubmitting} fullWidth>
            {isSubmitting ? 'Saving…' : mode === 'create' ? 'Create' : 'Save Changes'}
          </Button>
          {enableDelete && mode === 'edit' && (
            <Button color="error" variant="outlined" onClick={handleDelete} disabled={isDeleting} fullWidth>
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
    <ConfirmDialogPortal />
    </>
  );
}
